<template>
  <div
    class="timer-display-hero"
    :class="[variantClass, { 'server-desync': !serverSync }]"
    data-testid="timer-display-hero"
  >
    <div class="relative flex flex-col items-center gap-3 w-full">
      <!-- Arena Clock Timer -->
      <div
        class="timer-circle"
        :class="[
          timerPhaseClass,
          {
            'timer-urgent': isUrgent,
            'timer-critical': isCritical,
            'timer-paused': isPaused,
          },
        ]"
        role="timer"
        aria-live="polite"
        :aria-atomic="timerPhase === 'crisis'"
        :aria-label="timerAriaLabel"
      >
        <!-- Sync Status Icon - Hidden for Hero Design -->
        <div
          class="sync-status-icon hidden!"
          :class="{
            'sync-connected': serverSync,
            'sync-disconnected': !serverSync,
          }"
          role="status"
          :aria-label="
            serverSync
              ? STRINGS.status.connection.syncConnected
              : STRINGS.status.connection.syncDisconnected
          "
        >
          <i :class="serverSync ? 'pi pi-wifi' : 'pi pi-wifi'" aria-hidden="true"></i>
        </div>

        <!-- Hero Timer Number Display (NO SECONDS TEXT) -->
        <div class="timer-content text-center relative z-1">
          <span
            v-if="!isPaused"
            class="timer-display-number block text-center"
            :class="[colorClass, timerPhaseClass]"
          >
            {{ displayTime }}
          </span>

          <!-- Paused state display -->
          <div v-if="isPaused" class="timer-paused-display flex flex-col items-center gap-2">
            <AppIcon
              name="pause"
              :size="48"
              color="var(--color-arena-text-muted)"
              class="pause-icon"
            />
            <span class="text-[2.5rem] font-black text-gray-500 tracking-widest">{{
              STRINGS.ui.labels.paused
            }}</span>
            <span class="text-[1.8rem] font-semibold text-gray-400">{{ displayTime }}s</span>
          </div>

          <!-- Screen reader text with urgency -->
          <span class="sr-only">
            {{
              isPaused
                ? STRINGS.templates.timerPausedAt(displayTime)
                : timerPhase === 'crisis'
                  ? STRINGS.templates.urgentTimeRemaining(displayTime)
                  : timerPhase !== 'confident'
                    ? STRINGS.templates.timeRunningShort(displayTime)
                    : STRINGS.templates.timeRemainingAriaLabel(displayTime)
            }}
          </span>
        </div>
      </div>

      <!-- Server Sync Status -->
      <div v-if="!serverSync" class="sync-warning" role="alert">
        <i class="pi pi-exclamation-triangle" aria-hidden="true"></i>
        <span class="sr-only">Warning:</span>
        {{ STRINGS.status.connection.connectionLostTimer }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { STRINGS } from '@/constants/strings';
import AppIcon from '@/components/ui/icons/AppIcon.vue';

interface Props {
  timeRemaining: number; // Milliseconds remaining (0-30000)
  isActive: boolean; // Timer is counting down
  isPaused?: boolean; // Timer is paused (both players submitted)
  serverSync: boolean; // Server time synchronization status
  variant?: 'hero' | 'card' | 'desktop'; // Sizing context; 'hero' = standalone (default)
}

const props = withDefaults(defineProps<Props>(), {
  timeRemaining: 0,
  isActive: false,
  isPaused: false,
  serverSync: true,
  variant: 'hero',
});

const variantClass = computed(() => `timer-variant-${props.variant}`);

/**
 * Computed properties for display and animation
 */
const displayTime = computed(() => Math.max(0, Math.ceil(props.timeRemaining / 1000)));

const isUrgent = computed(() => props.timeRemaining <= 10000); // Final 10 seconds
const isCritical = computed(() => props.timeRemaining <= 5000); // Final 5 seconds

/**
 * Timer Phase State Management (New Hero Feature)
 * confident: 30-16s, tension: 15-11s, pressure: 10-6s, crisis: 5-0s
 */
const timerPhase = computed(() => {
  const seconds = Math.ceil(props.timeRemaining / 1000);
  if (seconds >= 16) return 'confident'; // Green, calm
  if (seconds >= 11) return 'tension'; // Amber, attention
  if (seconds >= 6) return 'pressure'; // Red, urgent
  return 'crisis'; // Critical red, dramatic
});

const timerPhaseClass = computed(() => `timer-${timerPhase.value}`);

/**
 * Enhanced ARIA label with urgency context
 */
const timerAriaLabel = computed(() => {
  if (timerPhase.value === 'crisis')
    return STRINGS.templates.urgentTimeRemaining(displayTime.value);
  if (timerPhase.value !== 'confident')
    return STRINGS.templates.timeRunningShort(displayTime.value);
  return STRINGS.templates.timeRemainingAriaLabel(displayTime.value);
});

/**
 * Color class for UI styling based on timer phases
 * Enhanced 4-tier progression aligned with design system
 */
const colorClass = computed(() => {
  const seconds = Math.ceil(props.timeRemaining / 1000);
  if (seconds >= 21) return 'timer-success'; // Green 30-21s (maps to confident)
  if (seconds >= 11) return 'timer-warning'; // Amber 20-11s (maps to tension)
  if (seconds >= 6) return 'timer-error'; // Red 10-6s (maps to pressure)
  return 'timer-critical'; // Critical red 5-0s (maps to crisis)
});
</script>

<style scoped>
.timer-display-hero {
  --timer-confident: var(--color-timer-confident);
  --timer-tension: var(--color-timer-tension);
  --timer-pressure: var(--color-timer-pressure);
  --timer-crisis: var(--color-timer-crisis);

  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  width: 100%;
  position: relative;
}

/* No longer circular — just a flex centering wrapper for the number */
.timer-circle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  position: relative;
  transition:
    filter var(--animate-duration-normal) var(--ease-gentle),
    opacity var(--animate-duration-normal) var(--ease-gentle);
}

/* Radial orb glow behind the timer number */
.timer-circle::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  border-radius: 50%;
  pointer-events: none;
  z-index: 0;
}

.timer-circle.timer-confident::before {
  width: 160px;
  height: 160px;
  background: radial-gradient(circle, rgba(16, 185, 129, 0.25) 0%, transparent 70%);
  animation: orb-pulse 3s ease-in-out infinite;
}

.timer-circle.timer-tension::before {
  width: 180px;
  height: 180px;
  background: radial-gradient(circle, rgba(245, 158, 11, 0.3) 0%, transparent 70%);
  animation: orb-pulse 2.5s ease-in-out infinite;
}

.timer-circle.timer-pressure::before {
  width: 200px;
  height: 200px;
  background: radial-gradient(circle, rgba(239, 68, 68, 0.35) 0%, transparent 70%);
  animation: orb-pulse-urgent 1.2s ease-in-out infinite;
}

.timer-circle.timer-crisis::before {
  width: 220px;
  height: 220px;
  background: radial-gradient(circle, rgba(220, 38, 38, 0.45) 0%, transparent 70%);
  animation: orb-pulse-crisis 0.8s ease-in-out infinite;
}

.timer-circle.timer-paused::before {
  animation: none;
  opacity: 0.4;
}

.timer-circle.timer-pressure {
  /* Static glow: rasterized once, composited with the scale heartbeat below
     (animating the filter value would re-paint every frame). */
  filter: drop-shadow(0 0 45px rgba(239, 68, 68, 0.6));
  animation: urgent-dramatic 1.2s ease-in-out infinite;
}

.timer-circle.timer-crisis {
  filter: drop-shadow(0 0 50px rgba(220, 38, 38, 0.9));
  animation: crisis-heartbeat 0.8s ease-in-out infinite;
}

.timer-circle.timer-paused {
  filter: grayscale(0.3) brightness(0.7);
  animation: none !important;
  opacity: 0.8;
}

.timer-display-number {
  font-family: var(--font-display);
  font-size: 7rem;
  font-weight: 900;
  line-height: 1;
  letter-spacing: -0.02em;
  transition: color var(--animate-duration-fast) var(--ease-gentle);
}

.timer-display-number.timer-confident {
  color: var(--timer-confident);
  text-shadow: 0 0 8px rgba(16, 185, 129, 0.2);
}

.timer-display-number.timer-tension {
  color: var(--timer-tension);
  text-shadow: 0 0 8px rgba(245, 158, 11, 0.2);
}

.timer-display-number.timer-pressure {
  color: var(--timer-pressure);
  text-shadow: 0 0 8px rgba(239, 68, 68, 0.2);
}

.timer-display-number.timer-crisis {
  color: var(--timer-crisis);
  text-shadow: 0 0 8px rgba(220, 38, 38, 0.2);
}

.pause-icon {
  animation: pausePulse 2s ease-in-out infinite;
}

@keyframes pausePulse {
  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.6;
    transform: scale(0.95);
  }
}

.sync-status-icon {
  position: absolute;
  top: 10%;
  right: 10%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  z-index: 5;
  transition:
    background-color var(--animate-duration-normal) var(--ease-gentle),
    color var(--animate-duration-normal) var(--ease-gentle),
    box-shadow var(--animate-duration-normal) var(--ease-gentle),
    transform var(--animate-duration-normal) var(--ease-gentle);
  transform-origin: center;
}

.sync-status-icon.sync-connected {
  background: var(--color-accent-600);
  color: white;
  box-shadow: 0 2px 8px rgba(0, 230, 58, 0.3);
  transform: scale(1);
  animation: syncConnectedPulse 2s ease-in-out infinite;
}

.sync-status-icon.sync-disconnected {
  background: var(--color-error-600);
  color: white;
  box-shadow: 0 2px 8px rgba(230, 21, 60, 0.3);
  animation: syncWarningPulse 1.2s ease-in-out infinite;
  transform: scale(1.1);
}

.sync-status-icon .pi {
  font-size: 16px;
  line-height: 1;
}

.sync-warning {
  position: absolute;
  bottom: -1.5rem;
  left: 50%;
  transform: translateX(-50%);
  background: var(--color-energy-warning);
  color: var(--color-arena-void);
  font-size: 0.75rem;
  padding: 0.5rem 0.75rem;
  border-radius: 6px;
  border: 1px solid var(--color-energy-warning);
  display: flex;
  align-items: center;
  gap: 0.25rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  z-index: 10;
  white-space: nowrap;
}

.sync-warning .pi {
  font-size: 0.75rem;
}

@keyframes syncConnectedPulse {
  0%,
  100% {
    transform: scale(1);
    opacity: 1;
    box-shadow: 0 2px 8px rgba(0, 230, 58, 0.3);
  }
  50% {
    transform: scale(1.05);
    opacity: 0.9;
    box-shadow: 0 2px 12px rgba(0, 230, 58, 0.5);
  }
}

@keyframes syncWarningPulse {
  0%,
  100% {
    transform: scale(1.1);
    opacity: 1;
  }
  50% {
    transform: scale(1.2);
    opacity: 0.7;
    box-shadow: 0 2px 16px rgba(230, 21, 60, 0.7);
  }
}

@media (max-width: 639px) {
  .timer-display-number {
    font-size: 5rem;
  }

  .sync-status-icon {
    width: 24px;
    height: 24px;
  }

  .sync-status-icon .pi {
    font-size: 12px;
  }
}

@media (min-width: 640px) {
  .timer-display-number {
    font-size: 6.5rem;
  }
}

@media (min-width: 1440px) {
  .timer-display-number {
    font-size: 9rem;
  }
}

/* Responsive orb sizing */
@media (max-width: 639px) {
  .timer-circle.timer-confident::before {
    width: 120px;
    height: 120px;
  }
  .timer-circle.timer-tension::before {
    width: 140px;
    height: 140px;
  }
  .timer-circle.timer-pressure::before {
    width: 150px;
    height: 150px;
  }
  .timer-circle.timer-crisis::before {
    width: 160px;
    height: 160px;
  }
}

@media (min-width: 1440px) {
  .timer-circle.timer-confident::before {
    width: 200px;
    height: 200px;
  }
  .timer-circle.timer-tension::before {
    width: 220px;
    height: 220px;
  }
  .timer-circle.timer-pressure::before {
    width: 240px;
    height: 240px;
  }
  .timer-circle.timer-crisis::before {
    width: 260px;
    height: 260px;
  }
}

@media (prefers-contrast: high) {
  .timer-display-number {
    text-shadow: none;
  }

  .timer-circle::before {
    display: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .timer-circle.timer-pressure,
  .timer-circle.timer-crisis {
    animation: none !important;
    border: 3px solid currentColor;
    box-shadow: 0 0 0 2px rgba(220, 38, 38, 0.3);
  }

  .timer-circle::before {
    animation: none !important;
  }

  .sync-status-icon.sync-disconnected,
  .sync-status-icon.sync-connected {
    animation: none;
    transform: scale(1) !important;
  }

  .pause-icon {
    animation: none;
  }

  .timer-display-number,
  .sync-status-icon {
    transition-duration: 0.01ms;
  }
}

/* Orb keyframes */
@keyframes orb-pulse {
  0%,
  100% {
    opacity: 0.6;
    transform: translate(-50%, -50%) scale(1);
  }
  50% {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1.15);
  }
}

@keyframes orb-pulse-urgent {
  0%,
  100% {
    opacity: 0.6;
    transform: translate(-50%, -50%) scale(1);
  }
  50% {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1.2);
  }
}

@keyframes orb-pulse-crisis {
  0%,
  100% {
    opacity: 0.5;
    transform: translate(-50%, -50%) scale(1);
  }
  15% {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1.25);
  }
  30% {
    opacity: 0.7;
    transform: translate(-50%, -50%) scale(0.95);
  }
  45% {
    opacity: 0.9;
    transform: translate(-50%, -50%) scale(1.15);
  }
}

.timer-display-hero:focus-visible {
  outline: 3px solid var(--color-primary-500);
  outline-offset: 2px;
}

/* ── Sizing variants ──────────────────────────────────────────────────────
   'card' and 'desktop' seat the numeral inside the in-round arena card at a
   fixed size (no responsive shrink) and a single orb size per layout. The
   leading `.timer-display-hero` raises specificity above the phase and
   media-query rules so the variant wins regardless of source order. */
.timer-display-hero.timer-variant-card .timer-display-number {
  font-size: 84px;
}
.timer-display-hero.timer-variant-card .timer-circle::before {
  width: 168px;
  height: 168px;
}

.timer-display-hero.timer-variant-desktop .timer-display-number {
  font-size: 104px;
}
.timer-display-hero.timer-variant-desktop .timer-circle::before {
  width: 220px;
  height: 220px;
}
</style>
